<template>
  <!-- Remote streams -->
  <div class="remote-streams rounded-2xl p-2 h-full">
    <video id="screencast-video" autoplay playsinline muted></video>
    <canvas id="screencast-canvas" class="hidden"></canvas>

    <SmartGlassConnectionToast
      v-if="smartGlassToastShow"
      @toggleSmartGlassToast="toggleSmartGlassToast"
    />

    <ul role="list" class="h-full w-full conference-grid" :style="{ '--row-count': rowCount }">
      <template v-if="participantsRemoteStreams.length > 0">
        <!-- Scroll left btn -->
        <button
          v-if="enlargeMode && scrollIndex > 0"
          @click="scrollRemoteStreams(-1)"
          class="relative row-span-2 cursor-pointer"
          type="button"
        >
          <ScrollArrowIcon />
        </button>

        <RemoteUsersTile
          v-for="(props, index) in participantsRemoteStreams"
          :index="index"
          :key="props.jitsiUserId + props.userId"
          :userJitsiId="props.jitsiUserId"
          :remoteDisplayInfo="props"
          :setVideoAudioRefs="setVideoAudioRefs"
          :shouldShowRemoteUserVideo="shouldShowRemoteUserVideo"
          :moderatorId="moderatorId"
          :vuzixStreamId="vuzixStreamId"
          :tileId="props.jitsiUserId + props.userId"
          :gridDimension="gridDimension"
          :isClinicianStream="false"
          :enlargedJitsiId="enlargeMode"
        />

        <!-- Scroll right btn -->
        <button
          v-if="enlargeMode && countRemote - scrollIndex > 7"
          @click="scrollRemoteStreams(1)"
          class="relative col-span-2 row-span-2 cursor-pointer"
          type="button"
        >
          <ScrollArrowIcon class="scroll-btn-right" />
        </button>

        <SessionPausedOverlay
          v-if="sessionStore.isPhasePaused"
          :currentPhase="sessionStore.currentPhaseData.title"
          :isClinician="true"
        />
      </template>
      <template v-else>
        <NoParticipantsOverlay :currentPhase="sessionStore.currentPhaseData.title" />
      </template>
    </ul>
  </div>

  <!-- Control panel -->
  <div
    ref="controlPanel"
    :class="[{ active: sessionStore.isControlPanelOpened }, !enlargeMode ? 'visible' : 'hided']"
    class="control-panel rounded-2xl px-2 py-4 m-2"
    :style="{ '--row-height': rowHeight, '--control-panel-height': controlPanelHeight }"
  >
    <!-- Stethoscope Guidance Popup -->
    <StethoscopeGuidancePopup v-if="showStethGuide" :rowLocal="rowLocal" />

    <!-- Toggle panel button -->
    <button
      v-if="!enlargeMode"
      @click="togglePanel()"
      class="absolute w-full flex justify-center right-0 h-0 cursor-pointer"
      :class="[{ 'z-30': !sessionStore.isControlPanelOpened }]"
      type="button"
    >
      <CircleArrowDown
        class="relative h-7 w-7 bottom-8"
        :isUp="sessionStore.isControlPanelOpened"
        :longRotation="false"
      />
    </button>

    <ul role="list" class="h-full w-full grid gap-2" :class="localStreamsGridClass">
      <EnlargeableStreamPlayerLocal
        :index="0"
        :showTitle="true"
        :isMinimizable="false"
        :isClinicianStream="true"
        class="rounded camera"
        tileId="enlargeableStreamPlayerLocal"
        :jitsiId="myJitsiId"
        :muteMedia="muteMedia"
        :localStats="localConnectionQuality"
        :gridDimension="localStreamDimension"
        :speakingIndicatorAudioLevel="localAudioLevel"
        @toggleEnlargeMode="toggleEnlargeMode"
        :enlargedJitsiId="enlargeMode"
      />

      <!-- Stethoscope -->
      <PulseVisualizerTile
        v-if="shouldDisplayPulseVisualizer"
        :pulseVisualizerRef="pulseVisualizer"
      />

      <RemoteUsersTile
        v-for="(props, index) in clinicianRemoteStreams"
        :index="index"
        :key="props.jitsiUserId"
        :userJitsiId="props.jitsiUserId"
        :remoteDisplayInfo="props"
        :setVideoAudioRefs="setVideoAudioRefs"
        :shouldShowRemoteUserVideo="shouldShowRemoteUserVideo"
        :moderatorId="moderatorId"
        :vuzixStreamId="vuzixStreamId"
        :tileId="props.jitsiUserId"
        :gridDimension="localStreamDimension"
        :isClinicianStream="true"
        :muteMedia="muteMedia"
        @toggleEnlargeMode="toggleEnlargeMode"
        :enlargedJitsiId="enlargeMode"
        class="rounded camera"
      />

      <SmartGlassTile
        v-if="vuzixStream?.length"
        :index="0"
        :isClinicianStream="true"
        :userJitsiId="vuzixStream?.[0].jitsiUserId"
        :remoteDisplayInfo="vuzixStream?.[0]"
        :setVideoAudioRefs="setVideoAudioRefs"
        :gridDimension="localStreamDimension"
        @toggleEnlargeMode="toggleEnlargeMode"
        :enlargedJitsiId="enlargeMode"
        :muteMedia="muteMedia"
        tileId="smartGlass"
      />

      <template v-if="screenShareStream.length">
        <ScreenShareTile
          v-for="(screenshareProps, index) in screenShareStream"
          :index="index"
          :key="screenshareProps.jitsiUserId"
          :userJitsiId="screenshareProps?.jitsiUserId"
          :remoteDisplayInfo="screenshareProps"
          :setVideoAudioRefs="setVideoAudioRefs"
          :shouldShowRemoteUserVideo="shouldShowRemoteUserVideo"
          :gridDimension="localStreamDimension"
          @toggleEnlargeMode="toggleEnlargeMode"
          :enlargedJitsiId="enlargeMode"
          :muteMedia="muteMedia"
          tileId="enlargeableScreenshare"
        />
      </template>

      <SmartphoneTile
        v-if="smartphoneStream?.length"
        :index="0"
        :userJitsiId="smartphoneStream?.[0].jitsiUserId"
        :remoteDisplayInfo="smartphoneStream?.[0]"
        :setVideoAudioRefs="setVideoAudioRefs"
        :gridDimension="localStreamDimension"
        @toggleEnlargeMode="toggleEnlargeMode"
        :enlargedJitsiId="enlargeMode"
        :muteMedia="muteMedia"
        tileId="smartphone"
        class="rounded camera"
      />
    </ul>
  </div>
</template>

<script>
import { ref, reactive, onUnmounted, onMounted, watch, computed } from 'vue';
import useGroupCallHandlers from '@/Composables/useGroupCallHandlers';
import useExternalCameraComposable from '@/Composables/useExternalCameraComposable';
import { SocketioClient } from '@/Services/SocketioClient';
import JitsiService from '@/Services/JitsiService';
import { VICE_SESSION_PHASES, STETHOSCOPE_EVENTS, CAMERA_TYPES } from '@/Utils/vice-constants';
import SmartGlassTile from '@/Components/Session/Grid/SmartGlassTile.vue';
import SmartphoneTile from '@/Components/Session/Grid/SmartphoneTile.vue';
import ScreenShareTile from '@/Components/Session/Grid/ScreenShareTile.vue';
import PulseVisualizerTile from '@/Components/Session/Grid/PulseVisualizerTile.vue';
import EnlargeableStreamPlayerLocal from '@/Components/Session/Grid/EnlargeableStreamPlayerLocal.vue';
import RemoteUsersTile from '@/Components/Session/Grid/RemoteUsersTile.vue';
import SessionPausedOverlay from '@/Components/Session/Overlays/SessionPausedOverlay';
import useSessionStore from '@/Stores/useSessionStore';
import useIOTDevicesStore from '@/Stores/useIOTDevicesStore';
import useLocalStreamingDevicesStore from '@/Stores/useLocalStreamingDevicesStore';
import StethoscopeGuidancePopup from '@/Components/Toasts/StethoscopeGuidancePopup.vue';
import SmartGlassConnectionToast from '@/Components/Toasts/SmartGlassConnectionToast.vue';
import NoParticipantsOverlay from '@/Components/Session/Overlays/NoParticipantsOverlay.vue';
import CircleArrowDown from '@/Components/Icons/CircleArrowDown.vue';
import ScrollArrowIcon from '@/Components/Icons/ScrollArrowIcon.vue';

export default {
  props: {
    muteMedia: {
      type: Function,
    },
    clinicianDevices: {
      type: Object,
    },
  },
  emits: ['showHeader'],
  components: {
    StethoscopeGuidancePopup,
    SmartGlassTile,
    SmartphoneTile,
    ScrollArrowIcon,
    CircleArrowDown,
    RemoteUsersTile,
    ScreenShareTile,
    PulseVisualizerTile,
    SessionPausedOverlay,
    NoParticipantsOverlay,
    SmartGlassConnectionToast,
    EnlargeableStreamPlayerLocal,
  },
  setup(props, { emit }) {
    const videoAudioRefs = reactive({});
    const remoteUserProps = reactive({});

    const rowLocal = ref(1);
    const rowCount = ref(12);
    const scrollIndex = ref(0);
    const enlargeMode = ref(null);
    const isJoined = ref(false);
    const myJitsiId = ref('');
    const jitsiRoom = ref(null);
    const disconnect = ref(null);
    const rowHeight = ref('25%');
    const moderatorId = ref(null);
    const controlPanel = ref(null);
    const localAudioLevel = ref(0);
    const vuzixStreamId = ref(null);
    const pulseVisualizer = ref(null);
    const smartphoneStreamId = ref(null);
    const screenshareOngoing = ref(false);
    const localConnectionQuality = ref(0);
    const smartGlassToastShow = ref(false);
    const shouldDisplayVuzixStream = ref(true);

    const sessionStore = useSessionStore();
    const streamingDeviceStore = useIOTDevicesStore();
    const multiCameraDevicesStore = useLocalStreamingDevicesStore();

    const gridDimension = computed(() => calculateGridDimensions());
    const showStethGuide = computed(() => sessionStore.showStethGuide);
    const localStreamDimension = computed(() => calculateLocalStreamDimensions());
    const controlPanelHeight = computed(() => (rowLocal.value > 1 ? '97%' : '94%'));

    const internalCameraUser = computed(
      () => multiCameraDevicesStore.cameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM],
    );

    const screenShareStream = computed(() =>
      _.filter(remoteUserProps, (_props) => /ScreenshareUser/.test(_props.userId)),
    );

    const participantsRemoteStreams = computed(() => {
      const seenUserIds = new Set();

      // avoid extra devices duplication (?)
      return _.filter(remoteUserProps, (_props) => {
        const isDuplicateUserId = seenUserIds.has(_props.userId);
        if (!isDuplicateUserId) {
          seenUserIds.add(_props.userId);
        }

        return (
          !isDuplicateUserId &&
          _props.jitsiUserId !== moderatorId.value &&
          !/ScreenshareUser/.test(_props.userId) &&
          !/Stethoscope/.test(_props.userId) &&
          !/Otoscope/.test(_props.userId) &&
          !/Monitor/.test(_props.userId) &&
          !/Dermatoscope/.test(_props.userId) &&
          !/Exam Camera/.test(_props.userId) &&
          !/Smartphone/.test(_props.userId) &&
          !(/External/.test(_props.userId) && /Camera/.test(_props.userId)) &&
          _props.jitsiUserId !== vuzixStreamId.value &&
          _props.jitsiUserId !== smartphoneStreamId.value
        );
      });
    });

    const clinicianRemoteStreams = computed(() =>
      _.filter(
        remoteUserProps,
        (_props) =>
          /Monitor/.test(_props.userId) ||
          /Otoscope/.test(_props.userId) ||
          /Stethoscope/.test(_props.userId) ||
          /Dermatoscope/.test(_props.userId) ||
          /Exam Camera/.test(_props.userId) ||
          /External Camera/.test(_props.userId) ||
          _props.jitsiUserId === moderatorId.value,
      ),
    );

    const clinicianStreamsCount = computed(
      () =>
        _.filter(
          remoteUserProps,
          (_props) =>
            /vuzix/.test(_props.userId) ||
            /Monitor/.test(_props.userId) ||
            /Exam Camera/.test(_props.userId) ||
            /Otoscope/.test(_props.userId) ||
            /Smartphone/.test(_props.userId) ||
            /Stethoscope/.test(_props.userId) ||
            /Dermatoscope/.test(_props.userId) ||
            /ScreenshareUser/.test(_props.userId) ||
            /ScreenshareUser_1/.test(_props.userId) ||
            /ScreenshareUser_2/.test(_props.userId) ||
            /External Camera/.test(_props.userId) ||
            _props.jitsiUserId === moderatorId.value ||
            _props.jitsiUserId === smartphoneStreamId.value,
        ).length,
    );

    const countRemote = computed(() => participantsRemoteStreams.value.length);
    const countLocal = computed(() => clinicianStreamsCount.value + 1);

    function calculateLocalStreamDimensions() {
      const localGrid = Array(10).fill({ cols: 1, rows: 1, start: 1 });
      const enlargeLocalGrid = Array(10).fill({ cols: 2, rows: 2, start: 1 });

      return enlargeMode.value ? enlargeLocalGrid : localGrid;
    }

    const localStreamsGridClass = computed(() =>
      enlargeMode.value
        ? 'grid-rows-10 grid-cols-12 row-span-10'
        : `grid-rows-${rowLocal.value} grid-cols-4`,
    );

    function calculateGridDimensions() {
      const count = countRemote.value;
      const gridConfig = {
        0: Array(1).fill({ cols: 1, rows: 1, start: 1, row_start: 0 }),
        1: Array(1).fill({ cols: 60, rows: 1, start: 1, row_start: 0 }),
        2: Array(2).fill({ cols: 30, rows: 6, start: 1, row_start: 3 }),
        3: [
          ...Array(2).fill({ cols: 30, rows: 6, start: 1, row_start: 0 }),
          ...Array(1).fill({ cols: 30, rows: 6, start: 16, row_start: 0 }),
        ],
        4: Array(4).fill({ cols: 30, rows: 6, start: 1, row_start: 0 }),
        5: [
          ...Array(3).fill({ cols: 20, rows: 6, start: 1, row_start: 0 }),
          ...Array(2).fill({ cols: 30, rows: 6, start: 1, row_start: 0 }),
        ],
        6: Array(6).fill({ cols: 20, rows: 6, start: 1, row_start: 0 }),
        7: [
          ...Array(4).fill({ cols: 15, rows: 6, start: 1, row_start: 0 }),
          ...Array(3).fill({ cols: 20, rows: 6, start: 1, row_start: 0 }),
        ],
        8: Array(8).fill({ cols: 15, rows: 6, start: 1, row_start: 0 }),
        9: Array(9).fill({ cols: 20, rows: 4, start: 1, row_start: 0 }),
        10: [
          ...Array(4).fill({ cols: 15, rows: 4, start: 1, row_start: 0 }),
          ...Array(6).fill({ cols: 20, rows: 4, start: 1, row_start: 0 }),
        ],
        11: [
          ...Array(8).fill({ cols: 15, rows: 4, start: 1, row_start: 0 }),
          ...Array(3).fill({ cols: 20, rows: 4, start: 1, row_start: 0 }),
        ],
        12: Array(12).fill({ cols: 15, rows: 4, start: 1, row_start: 0 }),
        13: [
          ...Array(5).fill({ cols: 12, rows: 4, start: 1, row_start: 0 }),
          ...Array(8).fill({ cols: 15, rows: 4, start: 1, row_start: 0 }),
        ],
        14: [
          ...Array(10).fill({ cols: 12, rows: 4, start: 1, row_start: 0 }),
          ...Array(4).fill({ cols: 15, rows: 4, start: 1, row_start: 0 }),
        ],
        15: Array(15).fill({ cols: 12, rows: 4, start: 1, row_start: 0 }),
        16: Array(16).fill({ cols: 15, rows: 3, start: 1, row_start: 0 }),
        17: [
          ...Array(5).fill({ cols: 12, rows: 3, start: 1, row_start: 0 }),
          ...Array(12).fill({ cols: 15, rows: 3, start: 1, row_start: 0 }),
        ],
        18: [
          ...Array(10).fill({ cols: 12, rows: 3, start: 1, row_start: 0 }),
          ...Array(8).fill({ cols: 15, rows: 3, start: 1, row_start: 0 }),
        ],
        19: [
          ...Array(15).fill({ cols: 12, rows: 3, start: 1, row_start: 0 }),
          ...Array(4).fill({ cols: 15, rows: 3, start: 1, row_start: 0 }),
        ],
        20: [...Array(20).fill({ cols: 12, rows: 3, start: 1, row_start: 0 })],
        21: [
          ...Array(20).fill({ cols: 12, rows: 3, start: 1, row_start: 0 }),
          ...Array(1).fill({ cols: 1, rows: 1, start: 1, row_start: 0, hidden: true }),
        ],
      };

      rowCount.value = count > 1 || enlargeMode.value ? 12 : -1;
      const enlargeGrid = calculateEnlargedGridDimensions();
      const gridIndex = count > 21 ? 21 : count;

      return enlargeMode.value ? enlargeGrid : gridConfig[gridIndex || 0];
    }

    function calculateEnlargedGridDimensions() {
      let enlargeGrid;
      if (scrollIndex.value === 0) {
        enlargeGrid = [
          ...Array(1).fill({ cols: 8, rows: 2, start: 3, hidden: false }),
          ...Array(6).fill({ cols: 8, rows: 2, start: 1, hidden: false }),
          ...Array(14).fill({ cols: 8, rows: 2, start: 1, hidden: true }),
        ];
      } else {
        enlargeGrid = [
          ...Array(scrollIndex.value).fill({ cols: 8, rows: 2, start: 1, hidden: true }),
          ...Array(1).fill({ cols: 8, rows: 2, start: 3, hidden: false }),
          ...Array(6).fill({ cols: 8, rows: 2, start: 1, hidden: false }),
          ...Array(14).fill({ cols: 8, rows: 2, start: 1, hidden: true }),
        ];
      }

      return enlargeGrid;
    }

    const scrollRemoteStreams = (index) => {
      scrollIndex.value += index;
    };

    /**
     * When a user joins a conference, add the media track to the conference/room
     */
    const onConferenceJoined = async () => {
      const localTracks = internalCameraUser.value?.localTracks;
      const conferenceAudioTrack = internalCameraUser.value.jitsiRoom.getLocalAudioTrack();
      if (localTracks?.length) {
        isJoined.value = true;
        for (const newTrack of localTracks) {
          // Replace audio track if already exists
          if (conferenceAudioTrack === newTrack || conferenceAudioTrack?.type === newTrack?.type) {
            // eslint-disable-next-line no-await-in-loop
            await internalCameraUser.value.jitsiRoom?.replaceTrack(conferenceAudioTrack, newTrack);
          } else {
            // eslint-disable-next-line no-await-in-loop
            await internalCameraUser.value.jitsiRoom?.addTrack(newTrack);
          }
        }
      }
    };

    const resetMicState = async (shouldTurnOnMic) => {
      await multiCameraDevicesStore.resetMicState(shouldTurnOnMic);
    };

    const { events, commands } = useGroupCallHandlers({
      remoteUserProps,
      videoAudioRefs,
      room: jitsiRoom,
      onConferenceJoined,
      vuzixStreamId,
      moderatorId,
      localAudioLevel,
      localConnectionQuality,
      myJitsiId,
      resetMicState,
      smartphoneStreamId,
    });

    const checkIfAudioAndVideoTracksExist = () => {
      const trackTypes = [];
      internalCameraUser.value?.localTracks?.forEach((track) => {
        trackTypes.push(track.type);
      });

      return trackTypes.includes('audio') && trackTypes.includes('video');
    };

    const isReady = computed(() => jitsiRoom.value && checkIfAudioAndVideoTracksExist());
    watch(isReady, (isReadyToJoin) => {
      if (isReadyToJoin) {
        initializeRoom();
      }
    });

    watch(remoteUserProps, (remoteUsers) => {
      screenshareOngoing.value = Object.values(remoteUsers).some((userProps) =>
        isScreenshareUser(userProps.userId),
      );
    });

    const isVuzixConnected = computed(
      () =>
        !!(
          Object.keys(remoteUserProps).length &&
          vuzixStreamId &&
          Object.keys(remoteUserProps).includes(vuzixStreamId.value)
        ),
    );

    const isSmartphoneConnected = computed(
      () =>
        Object.keys(remoteUserProps).length &&
        smartphoneStreamId &&
        Object.keys(remoteUserProps).includes(smartphoneStreamId.value),
    );

    const vuzixStream = computed(() =>
      _.filter(remoteUserProps, (_props) => _props.jitsiUserId === vuzixStreamId.value),
    );
    const smartphoneStream = computed(() =>
      _.filter(remoteUserProps, (_props) => _props.jitsiUserId === smartphoneStreamId.value),
    );

    const canAddExternalCamerasToCall = computed(
      () =>
        sessionStore.currentPhase === VICE_SESSION_PHASES.CONSULTATION && sessionStore.hasLivePhase,
    );

    const externalCameraJitsiIds = computed(() =>
      Object.keys(multiCameraDevicesStore.allCameraTypes).map((cameraType) =>
        multiCameraDevicesStore.allCameraTypes[cameraType].jitsiRoom?.myUserId(),
      ),
    );

    const isMultiCameraStream = (userId) => externalCameraJitsiIds.value.includes(userId);

    // During the live consultation, the external cameras act as remote users.
    // We therefore need to get the videoAudioRef and assign to our local store so that
    // we can attach and remove tracks.
    const switchMultiCamera = (userId) => {
      // Todo: access camera from the store
      const activeCamera = _.filter(
        multiCameraDevicesStore.allCameraTypes,
        (_props) => _props.jitsiRoom?.myUserId() === userId,
      )[0];

      multiCameraDevicesStore.setCurrentActiveCameraType(activeCamera.name);
      multiCameraDevicesStore.openAVSettingsModal();
    };

    // Get multi camera name using jitsiId
    const getActiveMultiCamData = (jitsiId) =>
      _.filter(
        multiCameraDevicesStore.allCameraTypes,
        (_props) => _props.jitsiRoom?.myUserId() === jitsiId,
      )[0];

    // Toggle Display but keep video visible to attendees
    const toggleMultiCamera = (userId) => {
      remoteUserProps[userId].isVideoHidden = !remoteUserProps[userId].isVideoHidden;
    };

    const toggleMultiCamMicrophone = (userId) => {
      const activeCamera = getActiveMultiCamData(userId);
      multiCameraDevicesStore.toggleMicrophone(activeCamera.name);
    };

    const disconnectMultiCamera = async (userId) => {
      const activeCamera = getActiveMultiCamData(userId);
      await multiCameraDevicesStore.removeCamera(activeCamera.name);
    };

    watch(isVuzixConnected, (newVal, oldValue) => {
      streamingDeviceStore.devices.smartglasses.connected = newVal;
      streamingDeviceStore.devices.smartglasses.disconnecting = false;
      if (newVal) {
        streamingDeviceStore.closeSmartGlassesModal();
      } else if (oldValue && !newVal) {
        toggleSmartGlassToast(true);
      }
    });

    const toggleSmartGlassToast = (state = false) => {
      if (state) {
        smartGlassToastShow.value = state;
        setTimeout(() => {
          smartGlassToastShow.value = false;
        }, 3000);
      } else {
        smartGlassToastShow.value = state;
      }
    };

    watch(isSmartphoneConnected, (newVal) => {
      streamingDeviceStore.devices.smartphone.connected = newVal;
      streamingDeviceStore.devices.smartphone.disconnecting = false;
      if (newVal) {
        streamingDeviceStore.closeSmartphoneModal();
      }
    });

    watch(countLocal, (newVal) => {
      rowLocal.value = newVal <= 4 ? 1 : 2;
      rowHeight.value = countLocal.value < 5 ? '25%' : '50%';
    });

    onUnmounted(() => {
      try {
        disconnect.value();
      } catch (error) {
        console.error(error);
      }
    });

    onMounted(async () => {
      const { room, disconnect: disconnectJitsi } = await JitsiService().makeConnection(
        sessionStore.sessionId,
        events,
        commands,
      );
      jitsiRoom.value = room;
      disconnect.value = disconnectJitsi;
      multiCameraDevicesStore.setActiveJitsiRoom(CAMERA_TYPES.INTERNAL_WEBCAM, room);

      if (canAddExternalCamerasToCall.value && multiCameraDevicesStore.devicesInitialized) {
        await useExternalCameraComposable().handleExternalCameraConnection();
      }

      setTimeout(async () => {
        await multiCameraDevicesStore.initializeJitsi(sessionStore.sessionId);
      }, 100);

      // Add external audio and video devices from cache
      await multiCameraDevicesStore.addExternalDevicesFromCache(sessionStore.sessionId);

      togglePanel(true);

      try {
        streamingDeviceStore.iotSockets.stethoscope = await SocketioClient(sessionStore.sessionId);

        // TODO: Move to devices store
        streamingDeviceStore.iotSockets.stethoscope.on(STETHOSCOPE_EVENTS.SIGNAL, ({ signal }) => {
          streamingDeviceStore.devices.stethoscope.connected = true;
          pulseVisualizer.value?.feedPcmPlayer(signal);
        });

        streamingDeviceStore.iotSockets.stethoscope.on(
          STETHOSCOPE_EVENTS.DEVICE_DISCONNECTED,
          () => {
            streamingDeviceStore.devices.stethoscope.connected = false;
          },
        );
      } catch (error) {
        console.error(error);
      }
    });

    const togglePanel = (state = null) => {
      sessionStore.toggleControlPanel(state);
    };

    const toggleEnlargeMode = (jitsiId) => {
      enlargeMode.value = jitsiId;
      if (jitsiId) {
        sessionStore.toggleControlPanel(true);
      }
      emit('showHeader', !enlargeMode.value);
    };

    const setVideoAudioRefs = (el, id) => {
      if (!videoAudioRefs[id]) {
        videoAudioRefs[id] = [];
      }
      if (el) {
        videoAudioRefs[id].push(el);
      }
    };

    const initializeRoom = () => {
      if (internalCameraUser.value?.localTracks) {
        jitsiRoom.value?.setDisplayName(
          `${sessionStore.authUser?.name}-Clinician-${sessionStore.authUser.id}`,
        );
        const profilePhoto = sessionStore.authUser?.profile_photo_url
          ? sessionStore.authUser.static_avatar_url
          : null;
        jitsiRoom.value?.setLocalParticipantProperty('avatar', profilePhoto);
        jitsiRoom.value?.setLocalParticipantProperty('isClinician', true);
        jitsiRoom.value?.join();
        myJitsiId.value = jitsiRoom.value?.myUserId();
      }
    };

    // Ensure disconnection where there's a refresh.
    // window.onbeforeunload = disconnect;

    // UI data and methods
    const totalParticipants = computed(() => Object.keys(remoteUserProps).length);

    const currentUserProfilePhoto = (jitsiId, userId) => {
      if (sessionStore.sessionDetails?.details?.clinician_id == userId) {
        remoteUserProps[jitsiId].photo =
          sessionStore.sessionDetails?.details?.clinician?.profile_photo_url;
      } else {
        const currentUser = sessionStore.sessionDetails.users.find(
          (user) => user.user_id == userId,
        );
        if (currentUser) {
          remoteUserProps[jitsiId].photo = currentUser.profile_photo_url;
        }
      }
    };

    const isScreenshareUser = (userId) => /ScreenshareUser/.test(userId);

    const isRemoteParticipantStream = (id) =>
      id !== moderatorId.value &&
      id !== vuzixStreamId.value &&
      !Object.hasOwn(remoteUserProps[id], 'screenshareOff');

    const shouldShowModeratorVideo = (moderatorJitsiId) =>
      remoteUserProps?.[moderatorJitsiId]?.video && !sessionStore.isPutOnHold;

    const moderatorHasVideoAndSessionIsOngoing = (moderatorJitsiId) =>
      !remoteUserProps?.[moderatorJitsiId]?.video ||
      sessionStore.isPutOnHold ||
      sessionStore.isPhasePaused;

    const shouldDisplayPulseVisualizer = computed(
      () =>
        sessionStore.currentPhase === VICE_SESSION_PHASES.CONSULTATION &&
        streamingDeviceStore.devices.stethoscope.connected,
    );

    const shouldShowRemoteUserVideo = (participantJitsiId) =>
      remoteUserProps?.[participantJitsiId]?.video &&
      !remoteUserProps?.[participantJitsiId]?.isVideoHidden &&
      !sessionStore.isPutOnHold &&
      !sessionStore.isPhasePaused;

    const getStreamPlayerRemoteName = (id) =>
      isMultiCameraStream(id) ? remoteUserProps[id].userId : remoteUserProps[id].name;

    return {
      rowCount,
      rowLocal,
      rowHeight,
      myJitsiId,
      moderatorId,
      vuzixStream,
      enlargeMode,
      countRemote,
      scrollIndex,
      controlPanel,
      sessionStore,
      vuzixStreamId,
      gridDimension,
      showStethGuide,
      remoteUserProps,
      localAudioLevel,
      pulseVisualizer,
      isVuzixConnected,
      smartphoneStream,
      totalParticipants,
      screenShareStream,
      controlPanelHeight,
      screenshareOngoing,
      smartGlassToastShow,
      localStreamDimension,
      streamingDeviceStore,
      localStreamsGridClass,
      isSmartphoneConnected,
      localConnectionQuality,
      clinicianRemoteStreams,
      multiCameraDevicesStore,
      shouldDisplayVuzixStream,
      participantsRemoteStreams,
      shouldDisplayPulseVisualizer,
      VICE_SESSION_PHASES,
      togglePanel,
      toggleEnlargeMode,
      isScreenshareUser,
      setVideoAudioRefs,
      toggleMultiCamera,
      switchMultiCamera,
      scrollRemoteStreams,
      isMultiCameraStream,
      disconnectMultiCamera,
      toggleSmartGlassToast,
      currentUserProfilePhoto,
      shouldShowModeratorVideo,
      toggleMultiCamMicrophone,
      isRemoteParticipantStream,
      shouldShowRemoteUserVideo,
      getStreamPlayerRemoteName,
      moderatorHasVideoAndSessionIsOngoing,
    };
  },
};
</script>

<style lang="scss" scoped>
.vuzixToggleButton {
  z-index: 2;
}

.text-shadow {
  text-shadow: 0 0 2px black;
}

.shadow-filter {
  filter: drop-shadow(0 0 2px black);
}

#screencast-video {
  position: absolute;
  z-index: -1;
  opacity: 0;
}

.control-panel {
  position: sticky;
  bottom: 0;
  left: 0;
  width: auto;
  z-index: 20;
  color: #fff;
  transform: translateY(var(--control-panel-height));
}

.control-panel.visible {
  height: var(--row-height);
  border: 2px solid #f5f5f5;
  background-color: #f5f5f5;
  transition: transform 0.6s cubic-bezier(0.76, 0.01, 0.31, 1);
}

.control-panel.hided {
  height: 84%;
}

.control-panel.active {
  transform: translateY(0);
}

.conference-grid {
  display: grid;
  height: 94%;
  gap: 0.5rem;
  grid-template-columns: repeat(60, minmax(10px, 1fr));
  grid-template-rows: repeat(var(--row-count), minmax(5px, 1fr));
}

.scroll-btn-right {
  transform: rotate(180deg);
}
</style>
